import { Box, Flex, H6, Select, Text } from '@lucidhq/lucidium';
import { useEffect, useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { DropDownResponse } from '@/utils/types';

interface TraslationsSectionProps {
  languages: DropDownResponse['data'];
  translations?: Translation[];
  translationLanguages?: string[];
  logoUrl?: string;
}

type LanguageOption = {
  key: string;
  value: string;
};

type SelectOption = {
  label: string;
  value: string;
};

type Translation = {
  language_code: string;
  logo_alternative_text: string;
  reward_description: string;
  public_name: string;
  redemption_instructions: string;
  terms_conditions: string;
};

type Translations = {
  [key: string]: Translation;
};

export const PaymentTranslationsPreview = ({
  languages = [],
  translations = [],
  translationLanguages = [],
  logoUrl,
}: TraslationsSectionProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');

  const translationsByCode = useMemo(() => {
    if (translations?.length) {
      return translations.reduce((acc: Translations, curr: Translation) => {
        return {
          ...acc,
          [curr.language_code]: curr,
        };
      }, {});
    }
    return {};
  }, [translations]);

  const translationLanguagesOptions = useMemo(() => {
    return translationLanguages
      .map(
        (languageCode: string) =>
          languages?.find(({ key }) => key === languageCode)
      )
      .filter((language): language is LanguageOption => !!language)
      .map((language: LanguageOption) => ({
        label: language.value,
        value: language.key,
      }));
  }, [languages, translationLanguages]);

  useEffect(() => {
    if (translationLanguagesOptions.length) {
      setSelectedLanguage(translationLanguagesOptions[0].value);
    }
  }, [translationLanguagesOptions]);

  return (
    <Box>
      <Box lss={{ my: 'lg' }}>
        {logoUrl && (
          <img
            src={logoUrl}
            alt="logo"
            style={{ maxHeight: '50px', maxWidth: '200px' }}
            data-testid="logo"
          />
        )}
      </Box>
      <Box lss={{ mb: 'lg', width: 250 }}>
        <Select
          labelKey="label"
          placeholder="Select language to preview"
          options={translationLanguagesOptions as SelectOption[]}
          value={selectedLanguage}
          onChange={(value: any) => setSelectedLanguage(value)}
          valueKey="value"
        />
      </Box>
      <Box>
        <Flex lss={{ gap: '30%' }}>
          <Box lss={{ mb: 'xl' }}>
            <Box>
              <H6>{translationsByCode[selectedLanguage]?.public_name}</H6>
            </Box>
          </Box>
          <H6 lss={{ wordBreak: 'break-word' }}>
            {translationsByCode[selectedLanguage]?.logo_alternative_text}
          </H6>
        </Flex>
        <Box lss={{ mb: 'xl', p: { margin: 0 } }}>
          <H6 lss={{ mb: 'sm' }}>Reward Description</H6>
          <Text>
            <Markdown remarkPlugins={[remarkGfm]}>
              {translationsByCode[selectedLanguage]?.reward_description}
            </Markdown>
          </Text>
        </Box>
        <Box lss={{ mb: 'xl', p: { margin: 0 } }}>
          <H6 lss={{ mb: 'sm' }}>Terms and Conditions</H6>
          <Text>
            <Markdown remarkPlugins={[remarkGfm]}>
              {translationsByCode[selectedLanguage]?.terms_conditions}
            </Markdown>
          </Text>
        </Box>
        <Box lss={{ mb: 'xl', p: { margin: 0 } }}>
          <H6 lss={{ mb: 'sm' }}>Redemption Instructions</H6>
          <Text>
            <Markdown remarkPlugins={[remarkGfm]}>
              {translationsByCode[selectedLanguage]?.redemption_instructions}
            </Markdown>
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
